// Step 2 of the client-side X12 pipeline: group step 1's flat segment array
// into the ISA▸GS[]▸ST[] tree the rest of the tool navigates. This module is
// deliberately tolerant of a malformed envelope (a stray segment outside any
// ST, a group never closed by a GE) — flagging that kind of problem is the
// validator's job (lib/validator.ts), not this one's. Structuring here never
// throws; a document that's too broken to structure meaningfully still
// yields whatever partial tree could be recovered.

import type {EdiElementValue, EdiSegment} from './tokenizer';

export interface EdiTransaction {
  stSegment: EdiSegment;
  /** ST01 — the transaction set code, e.g. "850", "810". Read once here so
   * callers never need to re-index into stSegment.elements themselves. */
  transactionSetCode: string;
  /** Every segment between ST and SE, exclusive of both. */
  segments: EdiSegment[];
  seSegment?: EdiSegment;
}

export interface EdiGroup {
  /** Absent only for a malformed interchange where an ST appears with no
   * enclosing GS — the transaction is still surfaced rather than dropped. */
  gsSegment?: EdiSegment;
  transactions: EdiTransaction[];
  geSegment?: EdiSegment;
}

export interface EdiEnvelope {
  isaSegment?: EdiSegment;
  groups: EdiGroup[];
  ieaSegment?: EdiSegment;
}

function elementAsString(
  value: EdiElementValue | undefined
): string | undefined {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value[0] : value;
}

/**
 * Structures step 1's flat segment array into an ISA▸GS[]▸ST[] tree.
 * Supports more than one GS group and more than one ST transaction per group.
 * A segment encountered outside any open ST (a stray segment between GS and
 * the first ST, for instance) is dropped rather than misattributed — the
 * validator reports on envelope-level irregularities like that separately.
 */
export function buildEnvelope(segments: EdiSegment[]): EdiEnvelope {
  const envelope: EdiEnvelope = {groups: []};

  let currentGroup: EdiGroup | undefined;
  let currentTransaction: EdiTransaction | undefined;

  for (const segment of segments) {
    switch (segment.id) {
      case 'ISA':
        envelope.isaSegment = segment;
        break;

      case 'GS':
        currentGroup = {gsSegment: segment, transactions: []};
        envelope.groups.push(currentGroup);
        currentTransaction = undefined;
        break;

      case 'GE':
        if (currentGroup) currentGroup.geSegment = segment;
        currentGroup = undefined;
        currentTransaction = undefined;
        break;

      case 'IEA':
        envelope.ieaSegment = segment;
        break;

      case 'ST':
        currentTransaction = {
          stSegment: segment,
          transactionSetCode: elementAsString(segment.elements[0]) ?? '',
          segments: [],
        };
        if (currentGroup) currentGroup.transactions.push(currentTransaction);
        else {
          // No open GS to attach to — still surface it as its own,
          // group-less entry so a malformed interchange doesn't silently
          // lose a whole transaction.
          currentGroup = {transactions: [currentTransaction]};
          envelope.groups.push(currentGroup);
        }
        break;

      case 'SE':
        if (currentTransaction) currentTransaction.seSegment = segment;
        currentTransaction = undefined;
        break;

      default:
        if (currentTransaction) currentTransaction.segments.push(segment);
        break;
    }
  }

  return envelope;
}

/** The inverse of buildEnvelope: flattens the tree back into ordered
 * segments — ISA, then for every group its GS, every transaction's
 * ST/body/SE, the group's GE, then the interchange's IEA. Missing
 * envelope/trailer segments (a malformed document) are simply skipped
 * rather than fabricated. */
export function flattenEnvelope(envelope: EdiEnvelope): EdiSegment[] {
  const segments: EdiSegment[] = [];
  if (envelope.isaSegment) segments.push(envelope.isaSegment);

  for (const group of envelope.groups) {
    if (group.gsSegment) segments.push(group.gsSegment);
    for (const transaction of group.transactions) {
      segments.push(transaction.stSegment);
      segments.push(...transaction.segments);
      if (transaction.seSegment) segments.push(transaction.seSegment);
    }
    if (group.geSegment) segments.push(group.geSegment);
  }

  if (envelope.ieaSegment) segments.push(envelope.ieaSegment);
  return segments;
}
